/**
 * Utilitários do Módulo de Compras
 * @version 2.1.0 - Fix fornecedor nome display (2026-02-25)
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

// Cada status tem: label, color (badge), dot (cor do ponto na legenda), descricao e grupo.
// Cores escolhidas para NÃO colidir dentro de um mesmo grupo (ex.: aprovado ≠ recebido).
export const statusConfig = {
  // === Cotação ===
  rascunho: { label: 'Rascunho', color: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400', grupo: 'cotacao', descricao: 'Em elaboração — ainda não enviada aos fornecedores' },
  aguardando_respostas: { label: 'Aguardando resposta', color: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500', grupo: 'cotacao', descricao: 'Enviada aos fornecedores, aguardando as propostas' },
  em_analise: { label: 'Analisando', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', grupo: 'cotacao', descricao: 'Propostas recebidas, em comparação na Mesa de Decisão' },
  analisando: { label: 'Analisando', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', grupo: 'cotacao', descricao: 'Propostas recebidas, em comparação na Mesa de Decisão' },
  aprovada_parcial: { label: 'Decisão em curso', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', grupo: 'cotacao', descricao: 'Parte dos itens já teve fornecedor escolhido' },
  aprovada: { label: 'Aprovada', color: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500', grupo: 'cotacao', descricao: 'Fornecedor escolhido — pedido(s) de compra gerado(s)' },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700', dot: 'bg-red-500', grupo: 'cotacao', descricao: 'Cotação cancelada' },
  expirada: { label: 'Expirada', color: 'bg-zinc-100 text-zinc-500', dot: 'bg-zinc-400', grupo: 'cotacao', descricao: 'Prazo de validade da cotação vencido' },
  // === Pedido de Compra ===
  pendente: { label: 'Pendente', color: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500', grupo: 'pedido', descricao: 'Aguardando aprovação interna' },
  aprovado: { label: 'Aprovado', color: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500', grupo: 'pedido', descricao: 'Aprovado internamente — pronto para envio ao fornecedor' },
  enviado: { label: 'Enviado', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', grupo: 'pedido', descricao: 'Enviado ao fornecedor, aguardando entrega' },
  recebido_parcial: { label: 'Receb. Parcial', color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', grupo: 'pedido', descricao: 'Parte da mercadoria foi recebida' },
  recebido: { label: 'Recebido', color: 'bg-green-100 text-green-700', dot: 'bg-green-500', grupo: 'pedido', descricao: 'Mercadoria recebida integralmente' },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-700', dot: 'bg-red-500', grupo: 'pedido', descricao: 'Pedido cancelado' },
};

export const StatusBadge = ({ status }) => {
  const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

// Legenda/índice de status recolhível. grupo: 'cotacao' | 'pedido'
export const StatusLegend = ({ grupo }) => {
  const [aberto, setAberto] = useState(false);
  // Evita duplicar "Analisando" (em_analise + analisando têm o mesmo label)
  const vistos = new Set();
  const itens = Object.values(statusConfig).filter((c) => {
    if (c.grupo !== grupo) return false;
    if (vistos.has(c.label)) return false;
    vistos.add(c.label);
    return true;
  });
  return (
    <div className="mb-4 border border-gray-200 rounded-lg bg-white" data-testid={`status-legend-${grupo}`}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
        data-testid={`status-legend-toggle-${grupo}`}
        aria-expanded={aberto}
      >
        <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <Info className="w-3.5 h-3.5" /> Legenda de status
        </span>
        {aberto ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {aberto && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-100 flex flex-wrap gap-x-4 gap-y-1.5">
          {itens.map((c) => (
            <div key={c.label} className="flex items-center gap-1.5" title={c.descricao}>
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${c.dot}`} />
              <span className="text-xs font-medium text-gray-700">{c.label}</span>
              <span className="text-xs text-gray-400 hidden sm:inline">— {c.descricao}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

import { formatCurrency as _formatCurrency, formatCurrencyShort as _formatCurrencyShort } from '../../lib/currencies';

export const formatCurrency = (value, moeda = 'BRL') => _formatCurrency(value, moeda);
export const formatCurrencyShort = (value, moeda = 'BRL') => _formatCurrencyShort(value, moeda);

export const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

/**
 * Obtém o nome do fornecedor com fallback adequado
 * Trata strings vazias e valores null/undefined
 */
export const getFornecedorNome = (forn) => {
  if (!forn) return 'Fornecedor não identificado';
  
  // Tentar campos de nome em ordem de prioridade
  const razaoSocial = forn.razao_social?.trim();
  const nome = forn.nome?.trim();
  const nomeFantasia = forn.nome_fantasia?.trim();
  const contatoNome = forn.contato_nome?.trim();
  const email = forn.email?.trim();
  const telefone = forn.telefone?.trim();
  
  if (razaoSocial) return razaoSocial;
  if (nome) return nome;
  if (nomeFantasia) return nomeFantasia;
  if (contatoNome) return `Contato: ${contatoNome}`;
  if (email) return email;
  if (telefone) return `Tel: ${telefone}`;
  
  return 'Fornecedor sem nome';
};

export const emptyCotacaoForm = {
  titulo: '',
  empresa_id: '',
  projeto_id: '',
  observacoes: '',
  data_limite_resposta: '',
  validade_dias: 30,
  itens: [],
  fornecedores: [],
  urgente: false,
};
